/**
 * features/hr/TaxTablesAdmin.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — Botswana PAYE tax-table management (Phase 1, Day 3).
 *
 * Admin-only. This is where the school keeps its PAYE rates current.
 * The compliance position (roadmap §5.3): Acepsle owns the engine's
 * correctness given a valid table; LKC owns keeping the active table
 * current. So this screen exists to let an admin:
 *
 *   - see exactly which table is ACTIVE (the one every pay run uses),
 *   - read its brackets,
 *   - add a NEW version (e.g. after a Budget Speech) and ACTIVATE it,
 *     which archives the previous version (never deletes it),
 *   - see the full version history,
 *   - sanity-check the engine with a live PAYE preview.
 *
 * Every create / activate writes an hrAuditLog entry (who, when,
 * before/after) — so "what rates did we use in May 2026" is always
 * answerable. Historical payslips stamp their own table id and never
 * recalculate; this screen only governs what NEW pay runs use.
 *
 * If no table exists yet, a one-click action seeds the confirmed
 * FY2026 table (BURS, unchanged for the year ending 30 June 2026).
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  Calculator, ShieldCheck, Plus, Save, Loader2, CheckCircle2,
  Archive, History, AlertTriangle, Trash2,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip, AccentButton, glassFieldClass, FormModal } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { useHrData } from '@/features/hr/useHrData';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import {
  FY2026_TAX_TABLE,
  computeAnnualPAYE,
  monthlyPAYE,
  marginalRatePct,
  round2,
  fmtPula,
  type HrTaxTable,
  type TaxBracket,
} from '@/lib/hrPayroll';

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function TaxTablesAdmin() {
  const { currentUser, userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const data = useHrData();
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  if (!perms.canManageTaxTables) {
    return (
      <SoftCard hover={false}>
        <div className="flex items-center gap-3 py-4 text-gray-600 dark:text-gray-300">
          <ShieldCheck size={20} className="text-gray-400" />
          <p className="text-sm">Tax-table management is restricted to administrators.</p>
        </div>
      </SoftCard>
    );
  }

  if (data.loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label="Loading tax tables…" />
      </div>
    );
  }

  const tables = [...data.taxTables].sort((a, b) => b.activeFrom.localeCompare(a.activeFrom));
  const active = data.activeTaxTable;

  const actor = { id: currentUser?.uid ?? 'unknown', email: currentUser?.email };

  async function seedFY2026() {
    setSeeding(true);
    setSaveModal({ show: true, status: 'saving', message: 'Loading FY2026 table…' });
    try {
      await hr.taxTables.set(FY2026_TAX_TABLE.id, { ...FY2026_TAX_TABLE });
      await recordHrAudit({
        action: 'taxTable.seed',
        actor,
        subjectId: FY2026_TAX_TABLE.id,
        after: { label: FY2026_TAX_TABLE.label, brackets: FY2026_TAX_TABLE.brackets },
      });
      setSaveModal({ show: true, status: 'success', message: 'FY2026 tax table loaded and active.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSeeding(false);
    }
  }

  async function activate(table: HrTaxTable) {
    setBusyId(table.id);
    setSaveModal({ show: true, status: 'saving', message: 'Activating table…' });
    try {
      // Archive whichever is currently active (if different).
      if (active && active.id !== table.id) {
        await hr.taxTables.update(active.id, { active: false, archivedAt: new Date().toISOString() });
      }
      await hr.taxTables.update(table.id, { active: true, archivedAt: undefined });
      await recordHrAudit({
        action: 'taxTable.activate',
        actor,
        subjectId: table.id,
        before: active ? { id: active.id, label: active.label } : undefined,
        after: { id: table.id, label: table.label },
      });
      setSaveModal({ show: true, status: 'success', message: `${table.label} is now active.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Calculator}
        title="PAYE Tax Tables"
        subtitle="Botswana income-tax brackets used by every pay run"
        actions={
          <AccentButton icon={Plus} onClick={() => setCreating(true)}>
            New table
          </AccentButton>
        }
      />

      {/* Active table */}
      {active ? (
        <SoftCard hover={false}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  Active
                </span>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">{active.label}</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Effective from {fmtDate(active.activeFrom)} · every pay run uses these rates until a new table is activated.
              </p>
            </div>
          </div>
          <BracketTable brackets={active.brackets} />
          <PayePreview table={active} />
        </SoftCard>
      ) : (
        <SoftCard hover={false}>
          <div className="flex flex-col items-start gap-3 py-2">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle size={18} />
              <p className="text-sm font-medium">No active tax table.</p>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The pay run needs an active table to compute PAYE. Load the confirmed
              Botswana FY2026 table (BURS, year ending 30 June 2026), then add new
              versions here whenever a Budget Speech changes the rates.
            </p>
            <AccentButton icon={seeding ? Loader2 : Save} disabled={seeding} onClick={seedFY2026}>
              {seeding ? 'Loading…' : 'Load standard FY2026 table'}
            </AccentButton>
          </div>
        </SoftCard>
      )}

      {/* Version history */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <History size={16} className="text-gray-500" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Version history</h3>
        </div>
        {tables.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 px-5 py-4">No tables on file yet.</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {tables.map((t) => (
              <div key={t.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-800 dark:text-gray-100 truncate">{t.label}</p>
                    {t.active ? (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Active</span>
                    ) : (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300">Archived</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Effective {fmtDate(t.activeFrom)} · {t.brackets.length} brackets
                    {t.archivedAt ? ` · archived ${fmtDate(t.archivedAt)}` : ''}
                  </p>
                </div>
                {!t.active && (
                  <button
                    onClick={() => activate(t)}
                    disabled={busyId === t.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-strong,#1b3a6b)] text-white hover:bg-[var(--accent-strong-hover,#162e52)] disabled:opacity-60"
                  >
                    {busyId === t.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    Activate
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </SoftCard>

      {creating && (
        <TableFormModal
          existingIds={tables.map((t) => t.id)}
          onClose={() => setCreating(false)}
          actor={actor}
        />
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </div>
  );
}

/* ==================================================================
   BRACKET TABLE (read-only display)
   ================================================================== */
function BracketTable({ brackets }: { brackets: TaxBracket[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            <th className="py-2 pr-4 font-medium">Annual income over</th>
            <th className="py-2 pr-4 font-medium">Up to</th>
            <th className="py-2 font-medium text-right">Rate on excess</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
          {brackets.map((b, i) => (
            <tr key={i}>
              <td className="py-2 pr-4 text-gray-700 dark:text-gray-200">P{fmtPula(b.from)}</td>
              <td className="py-2 pr-4 text-gray-700 dark:text-gray-200">{b.to === null ? '—' : `P${fmtPula(b.to)}`}</td>
              <td className="py-2 text-right font-medium text-gray-800 dark:text-gray-100">{b.ratePct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==================================================================
   PAYE PREVIEW — sanity-check the engine against the active table
   ================================================================== */
function PayePreview({ table }: { table: HrTaxTable }) {
  const [monthly, setMonthly] = useState('12000');
  const m = parseFloat(monthly) || 0;
  const annual = round2(m * 12);
  const annualTax = computeAnnualPAYE(annual, table);
  const monthTax = monthlyPAYE(m, table);
  const marg = marginalRatePct(annual, table);

  return (
    <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
        <Calculator size={14} /> PAYE preview
      </h4>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Monthly gross (BWP)</span>
          <input
            type="number"
            inputMode="decimal"
            value={monthly}
            onChange={(e) => setMonthly(e.target.value)}
            className={glassFieldClass('py-2 w-40')}
          />
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 min-w-[18rem]">
          <StatChip tone="blue" value={`P${fmtPula(annual)}`} label="Annual gross" />
          <StatChip tone="gold" value={`${marg}%`} label="Marginal rate" />
          <StatChip tone="red" value={`P${fmtPula(annualTax)}`} label="Annual PAYE" />
          <StatChip tone="emerald" value={`P${fmtPula(monthTax)}`} label="Monthly PAYE" />
        </div>
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
        Phase 1 computes PAYE on gross earnings (no pre-tax deductions). The figure shown
        here is what a pay run would withhold this month using the active table.
      </p>
    </div>
  );
}

/* ==================================================================
   NEW TABLE FORM — editable brackets, validated, saved then activatable
   ================================================================== */
type BracketDraft = { from: string; to: string; ratePct: string };

function TableFormModal({
  existingIds,
  onClose,
  actor,
}: {
  existingIds: string[];
  onClose: () => void;
  actor: { id: string; email?: string | null };
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [label, setLabel] = useState('');
  const [activeFrom, setActiveFrom] = useState(today);
  const [activateNow, setActivateNow] = useState(true);
  const [rows, setRows] = useState<BracketDraft[]>(
    FY2026_TAX_TABLE.brackets.map((b) => ({
      from: String(b.from),
      to: b.to === null ? '' : String(b.to),
      ratePct: String(b.ratePct),
    })),
  );
  const [saving, setSaving] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );
  const data = useHrData();

  function setRow(i: number, patch: Partial<BracketDraft>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((prev) => [...prev, { from: '', to: '', ratePct: '' }]);
  }
  function removeRow(i: number) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  const { brackets, error } = useMemo(() => validateBrackets(rows), [rows]);
  const idFromDate = activeFrom; // table id == effective-from date
  const idClash = existingIds.includes(idFromDate);
  const canSave = !!label.trim() && !!activeFrom && !error && !idClash && !saving;

  async function save() {
    if (!brackets) return;
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving tax table…' });
    try {
      const id = idFromDate;
      const willActivate = activateNow;
      // If activating, archive the current active table first.
      if (willActivate) {
        const current = data.activeTaxTable;
        if (current && current.id !== id) {
          await hr.taxTables.update(current.id, { active: false, archivedAt: new Date().toISOString() });
        }
      }
      const table: Omit<HrTaxTable, 'createdAt' | 'updatedAt'> = {
        id,
        label: label.trim(),
        brackets,
        active: willActivate,
        activeFrom: new Date(activeFrom).toISOString(),
        uploadedBy: actor.email ?? actor.id,
      };
      await hr.taxTables.set(id, { ...table });
      await recordHrAudit({
        action: willActivate ? 'taxTable.create+activate' : 'taxTable.create',
        actor,
        subjectId: id,
        after: { label: table.label, brackets },
      });
      setSaveModal({ show: true, status: 'success', message: willActivate ? 'Table saved and activated.' : 'Table saved.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="New PAYE tax table" icon={Calculator} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Label</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. FY ending 30 June 2027" className={glassFieldClass('py-2')} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Effective from</span>
            <input type="date" value={activeFrom} onChange={(e) => setActiveFrom(e.target.value)} className={glassFieldClass('py-2')} />
            {idClash && <span className="text-[11px] text-red-500 mt-0.5 block">A table with this effective-from date already exists.</span>}
          </label>
        </div>

        {/* Brackets editor */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Brackets (annual, BWP)</span>
            <button onClick={addRow} className="flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]">
              <Plus size={12} /> Add bracket
            </button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1fr_5rem_2rem] gap-2 text-[11px] text-gray-400 px-1">
              <span>Over</span><span>Up to (blank = top)</span><span>Rate %</span><span />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_5rem_2rem] gap-2 items-center">
                <input type="number" inputMode="decimal" value={r.from} onChange={(e) => setRow(i, { from: e.target.value })} className={glassFieldClass('py-1.5 text-sm')} />
                <input type="number" inputMode="decimal" value={r.to} onChange={(e) => setRow(i, { to: e.target.value })} placeholder="—" className={glassFieldClass('py-1.5 text-sm')} />
                <input type="number" inputMode="decimal" value={r.ratePct} onChange={(e) => setRow(i, { ratePct: e.target.value })} className={glassFieldClass('py-1.5 text-sm')} />
                <button onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500 p-1 justify-self-center" title="Remove">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          {error && (
            <p className="text-[11px] text-red-500 mt-2 flex items-center gap-1">
              <AlertTriangle size={12} /> {error}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
          <input type="checkbox" checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)} className="rounded" />
          Activate immediately
          {activateNow && <span className="text-xs text-amber-600 dark:text-amber-300 inline-flex items-center gap-1"><Archive size={12} /> archives the current active table</span>}
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">Cancel</button>
        <AccentButton icon={saving ? Loader2 : Save} disabled={!canSave} onClick={save}>
          {saving ? 'Saving…' : activateNow ? 'Save & activate' : 'Save table'}
        </AccentButton>
      </div>

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => {
          if (saveModal.status === 'success') onClose();
          else setSaveModal((m) => ({ ...m, show: false }));
        }}
      />
    </Modal>
  );
}

/** Validate the draft rows into clean brackets (ascending, one open top). */
function validateBrackets(rows: BracketDraft[]): { brackets: TaxBracket[] | null; error: string | null } {
  if (rows.length === 0) return { brackets: null, error: 'Add at least one bracket.' };
  const parsed: TaxBracket[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const from = parseFloat(r.from);
    const rate = parseFloat(r.ratePct);
    const to = r.to.trim() === '' ? null : parseFloat(r.to);
    if (!Number.isFinite(from) || from < 0) return { brackets: null, error: `Bracket ${i + 1}: "over" must be a number ≥ 0.` };
    if (!Number.isFinite(rate) || rate < 0) return { brackets: null, error: `Bracket ${i + 1}: rate must be a number ≥ 0.` };
    if (to !== null && (!Number.isFinite(to) || to <= from)) return { brackets: null, error: `Bracket ${i + 1}: "up to" must be greater than "over".` };
    parsed.push({ from: round2(from), to: to === null ? null : round2(to), ratePct: rate });
  }
  // exactly one open-ended (to === null), and it must be last
  const openCount = parsed.filter((b) => b.to === null).length;
  if (openCount !== 1) return { brackets: null, error: 'Exactly one bracket must have a blank "up to" (the top band).' };
  if (parsed[parsed.length - 1].to !== null) return { brackets: null, error: 'The open-ended (blank "up to") bracket must be last.' };
  // ascending & contiguous-ish: each from must equal previous to
  for (let i = 1; i < parsed.length; i++) {
    if (parsed[i].from !== parsed[i - 1].to) {
      return { brackets: null, error: `Bracket ${i + 1} should start (${parsed[i].from}) where bracket ${i} ends (${parsed[i - 1].to}).` };
    }
  }
  if (parsed[0].from !== 0) return { brackets: null, error: 'The first bracket must start at 0.' };
  return { brackets: parsed, error: null };
}

/* ==================================================================
   LOCAL MODAL
   ================================================================== */
function Modal({
  title,
  icon: Icon,
  children,
  onClose,
}: {
  title: string;
  icon?: typeof Calculator;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <FormModal onClose={onClose} icon={Icon} title={title}>
      {children}
    </FormModal>
  );
}
